import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Siren, ShieldCheck, MapPin, Phone, Clock, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TONE = {
  sos: "border-red-500 bg-red-50 dark:bg-red-950/30",
  overdue: "border-red-300 bg-red-50/60 dark:bg-red-950/20",
  active: "border-emerald-300 bg-emerald-50/50 dark:bg-emerald-950/20",
  ended: "border-border bg-card",
};
const LABEL = { sos: "SOS", overdue: "Missed check-in", active: "Active", ended: "Ended" };

function ago(iso) {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)}h ${m % 60}m ago`;
}

function jobLink(s) {
  if (!s.entity_id) return null;
  return s.entity_type === "emergency_job" ? `/emergency-jobs/${s.entity_id}` : `/jobs/${s.entity_id}`;
}

export default function LoneWorkerPage() {
  const [d, setD] = useState({ sessions: [], counts: {} });
  const [scope, setScope] = useState("open");
  const [resolve, setResolve] = useState({ open: false, session: null, note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/lone-worker/sessions?scope=${scope}&days=14`); setD(data); } catch { /* ignore */ }
  }, [scope]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const doResolve = async () => {
    setBusy(true);
    try {
      await API.post(`/lone-worker/sessions/${resolve.session.id}/resolve`, { note: resolve.note });
      toast.success("Marked as resolved");
      setResolve({ open: false, session: null, note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't resolve"); }
    finally { setBusy(false); }
  };

  const c = d.counts || {};
  return (
    <div className="space-y-6" data-testid="lone-worker-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="h-6 w-6" /> Lone Worker</h1>
          <p className="text-sm text-muted-foreground mt-1">Live check-in timers for operatives working alone. Missed check-ins and SOS alerts land here and in your notifications.</p>
        </div>
        <div className="flex gap-1 rounded-md border border-border p-0.5">
          {[["open", "Live"], ["all", "Last 14 days"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setScope(k)} className={`px-3 py-1.5 text-xs rounded ${scope === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`lw-scope-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="lw-counts">
        {[["sos", "SOS", "text-red-600"], ["overdue", "Missed check-in", "text-red-500"], ["active", "Active now", "text-emerald-600"], ["ended", scope === "open" ? "Ended" : "Ended (14d)", "text-muted-foreground"]].map(([k, l, cls]) => (
          <div key={k} className="rounded-lg border border-border bg-card p-3">
            <p className={`text-2xl font-bold ${cls}`} data-testid={`lw-count-${k}`}>{c[k] ?? 0}</p>
            <p className="text-xs text-muted-foreground">{l}</p>
          </div>
        ))}
      </div>

      {d.sessions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="lw-empty">
          {scope === "open" ? "No one is running a lone-worker timer right now." : "No sessions in the last 14 days."}
        </div>
      ) : (
        <div className="space-y-2">
          {d.sessions.map(s => {
            const loc = s.last_location || {};
            const link = jobLink(s);
            return (
              <div key={s.id} className={`rounded-lg border-2 p-4 ${TONE[s.status] || TONE.ended}`} data-testid={`lw-session-${s.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${s.status === "sos" ? "bg-red-600 text-white" : s.status === "overdue" ? "bg-red-500 text-white" : s.status === "active" ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`} data-testid={`lw-status-${s.id}`}>
                        {s.status === "sos" && <Siren className="inline h-3 w-3 mr-1" weight="fill" />}{LABEL[s.status] || s.status}
                      </span>
                      <p className="font-semibold">{s.user_name}</p>
                      {s.user_phone && <a href={`tel:${s.user_phone}`} className="text-xs text-primary inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {s.user_phone}</a>}
                    </div>
                    {s.entity_title && <p className="text-sm text-muted-foreground mt-1 truncate">{link ? <Link to={link} className="hover:underline">{s.entity_title}</Link> : s.entity_title}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Started {ago(s.started_at)} · every {s.interval_minutes} min</span>
                      <span>Last check-in {ago(s.last_check_in_at)}</span>
                      {s.status !== "ended" && <span>Due {new Date(s.next_due_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>}
                      {s.sos_at && <span className="text-red-600 font-medium">SOS {ago(s.sos_at)}{s.sos_note ? ` — "${s.sos_note}"` : ""}</span>}
                      {s.resolved_at && <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Resolved by {s.resolved_by}{s.resolved_note ? ` — ${s.resolved_note}` : ""}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {loc.lat != null && loc.lng != null && (
                      <a href={`https://maps.google.com/?q=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 hover:bg-muted" data-testid={`lw-map-${s.id}`}><MapPin className="h-3.5 w-3.5" /> Last location</a>
                    )}
                    {(s.status === "overdue" || s.status === "sos") && (
                      <Button size="sm" onClick={() => setResolve({ open: true, session: s, note: "" })} data-testid={`lw-resolve-${s.id}`}>Mark resolved</Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={resolve.open} onOpenChange={o => setResolve(r => ({ ...r, open: o }))}>
        <DialogContent data-testid="lw-resolve-dialog">
          <DialogHeader><DialogTitle>Resolve alert — {resolve.session?.user_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Confirm you've spoken to the operative (or otherwise know they're safe). This ends their session and lets them know it was handled.</p>
          <Textarea rows={3} value={resolve.note} onChange={e => setResolve(r => ({ ...r, note: e.target.value }))} placeholder="e.g. Called Sam — phone was on silent, all fine" data-testid="lw-resolve-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolve({ open: false, session: null, note: "" })}>Cancel</Button>
            <Button onClick={doResolve} disabled={busy} data-testid="lw-resolve-confirm">{busy ? "Saving…" : "Resolve"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
